<template>
  <v-dialog :value="visible" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="1100" persistent scrollable>
    <v-card class="dc-inspection">
      <v-card-title class="dc-header">
        <div>
          <h1 class="text-h6">{{ $t('damage_control.capture.title') }} · {{ $t(sideKey) }}</h1>
          <div class="text-body-2">{{ $t('damage_control.capture.version', { version: localInspection.version }) }}</div>
          <div v-if="trialLeft !== null" class="text-body-2">{{ $t('damage_control.capture.trial_left', { count: trialLeft }) }}</div>
        </div>
        <v-btn text data-testid="close" @click="close">{{ $t('damage_control.capture.close') }}</v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="errorText" type="warning" outlined role="alert" data-testid="inspection-error">{{ errorText }}</v-alert>
        <zone-capture v-if="selectedZone" :key="localInspection.id + ':' + selectedZone.key" ref="capture"
                      :inspection="localInspection" :zone="selectedZone" :history="attemptsFor(selectedZone)"
                      :preview-url="previewFor(selectedZone)" :departure-preview-url="departureFor(selectedZone)"
                      :entries="passport ? passport.entries || [] : []" :ghost-url="ghostFor(selectedZone)" :camera-deps="cameraDeps" :services="services"
                      @preview-error="refreshMedia" @attempt="rememberAttempt(selectedZone.key, $event)" @preview="rememberPreview"
                      @update:inspection="updateInspection" @busy="captureBusy = $event" @close="backToZones" />
        <div v-else class="dc-zones">
          <button v-for="zone in localInspection.zones" :key="zone.key" type="button" class="dc-zone-row"
                  :class="{ 'dc-zone-blocked': blockedZone === zone.key }" :data-zone="zone.key" :data-testid="'zone-' + zone.key"
                  :aria-invalid="blockedZone === zone.key ? 'true' : undefined" @click="selectZone(zone)">
            <img v-if="previewFor(zone)" :src="previewFor(zone)" @error="refreshMedia(previewFor(zone))" :alt="zone.title" class="dc-thumbnail" />
            <span class="dc-zone-copy">
              <strong>{{ zone.title }}</strong>
              <span v-if="zone.required" class="dc-required">{{ $t('damage_control.capture.required') }}</span>
              <span class="dc-hint">{{ zone.hint }}</span>
              <span class="dc-state" :data-testid="'zone-state-' + zone.key">{{ $t(stateKey(zone)) }}</span>
              <span v-if="reviewState(zone).checked" class="dc-hint" :data-testid="'zone-reviewed-' + zone.key">
                {{ $t('damage_control.review.checked') }}<template v-if="reviewOutcomeKey(zone)"> — {{ $t(reviewOutcomeKey(zone)) }}</template>
              </span>
              <span v-for="key in reasonKeys(zone)" :key="key" class="dc-hint">{{ $t(key) }}</span>
            </span>
          </button>
        </div>
        <section v-if="blockers.length && localInspection.status === 'in_progress'" class="mt-4" data-testid="completion-blockers">
          <h2 class="text-subtitle-1">{{ $t('damage_control.review.blockers') }}</h2>
          <ul>
            <li v-for="(blocker, index) in blockers" :key="blocker.zone_key + ':' + blocker.reason">
              <button type="button" class="dc-blocker" :disabled="captureBusy" :data-testid="'completion-blocker-' + index"
                      @click="selectedKey = blocker.zone_key">{{ blocker.title }}: {{ $t(blocker.reason) }}</button>
            </li>
          </ul>
        </section>
      </v-card-text>
      <v-card-actions class="dc-footer">
        <v-btn v-if="localInspection.status === 'in_progress'" color="primary" :loading="completing"
               :disabled="captureBusy || blockers.length > 0 || refreshRequired" data-testid="complete" @click="complete">
          {{ $t('damage_control.capture.complete') }}
        </v-btn>
        <v-btn v-if="refreshRequired" outlined data-testid="completion-refresh" :loading="completing" @click="refreshAfterFailure">{{ $t('damage_control.capture.refresh') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { damageControlApi, damageControlTrialLeft, parseDamageControlError, reportDamageControlFailure } from "@/api/damageControl";
import { completionBlockers, zoneReviewState, REVIEW_OUTCOME_KEYS } from "@/utils/damageControlReview";
import { shouldRefreshDamageControlPreview } from "@/utils/damageControlPreview";
import { damageControlAttempts } from "@/services/damageControlAttempts";
import ZoneCapture from "./ZoneCapture.vue";
import { STATE_KEYS, reasonKeys } from "./presentation";

export default {
  name: "InspectionScreen",
  components: { ZoneCapture },
  props: {
    inspection: { type: Object, required: true },
    cameraDeps: { type: Object, default: () => ({}) },
    services: { type: Object, default: () => ({}) },
  },
  data() {
    return { localInspection: this.inspection, visible: true, selectedKey: null, previews: {},
      act: null, passport: null, failedPreviewUrls: [], refreshRequired: false, completing: false, captureBusy: false, blockedZone: null, errorText: "", disposed: false, mediaGeneration: 0, actGeneration: 0, passportGeneration: 0 };
  },
  computed: {
    blockers() { return completionBlockers(this.localInspection); },
    selectedZone() { return (this.localInspection.zones || []).find(zone => zone.key === this.selectedKey); },
    sideKey() { return this.localInspection.side === "arrival" ? "damage_control.capture.arrival" : "damage_control.capture.departure"; },
    trialLeft() {
      const getters = this.$store && this.$store.getters;
      return damageControlTrialLeft(getters && getters.getCompany && getters.getCompany.company);
    },
  },
  watch: {
    inspection(value) {
      const changed = value.id !== this.localInspection.id;
      this.localInspection = value;
      if (changed) {
        this.mediaGeneration += 1;
        this.backToZones();
        this.releasePreviews();
        this.act = null;
        this.passport = null;
        this.blockedZone = null;
        this.loadMedia();
      }
    },
  },
  mounted() { this.loadMedia(); },
  beforeDestroy() { this.disposed = true; this.mediaGeneration += 1; this.releasePreviews(); },
  methods: {
    async refreshMedia(url) {
      if (!shouldRefreshDamageControlPreview(this.failedPreviewUrls, url)) return;
      const id = this.localInspection.id;
      await Promise.all([
        this.loadMedia(),
        damageControlApi.getInspection(this.axios, id).then(payload => {
          if (this.disposed || this.localInspection.id !== id) return;
          this.localInspection = payload;
          this.$emit("update:inspection", payload);
        }).catch(error => {
          if (!this.disposed && this.localInspection.id === id) this.errorText = reportDamageControlFailure(this, error, { silent: true });
        }),
      ]);
    },
    reviewState(zone) { return zoneReviewState(zone, this.localInspection.side); },
    reviewOutcomeKey(zone) { return REVIEW_OUTCOME_KEYS[this.reviewState(zone).outcome]; },
    async refreshAfterFailure() {
      this.completing = true;
      try {
        const payload = await damageControlApi.getInspection(this.axios, this.localInspection.id);
        if (this.disposed) return;
        this.updateInspection(payload);
        this.refreshRequired = false;
      } catch (error) { this.errorText = reportDamageControlFailure(this, error, { silent: true }); }
      finally { if (!this.disposed) this.completing = false; }
    },
    stateKey(zone) { return STATE_KEYS[zone.state] || STATE_KEYS.missing; },
    reasonKeys(zone) {
      const shot = zone.current_shot || zone.latest_shot;
      return reasonKeys(shot);
    },
    actZone(zone) { return this.act && (this.act.zones || []).find(item => item.key === zone.key); },
    previewFor(zone) {
      const current = zone.current_shot;
      if (!current) return "";
      if (current.preview_url) return current.preview_url;
      if (this.previews[current.id]) return this.previews[current.id];
      const pair = this.actZone(zone);
      const photo = pair && pair[this.localInspection.side];
      // The act can change while an inspection is open. Never attach a different shot's URL.
      return photo && photo.id === current.id ? photo.preview_url || "" : "";
    },
    departureFor(zone) {
      if (this.localInspection.side !== "arrival" || !this.localInspection.departure_inspection_id) return "";
      if (!this.act || !this.act.departure || this.act.departure.id !== this.localInspection.departure_inspection_id) return "";
      const pair = this.actZone(zone);
      return pair && pair.departure ? pair.departure.preview_url || "" : "";
    },
    ghostFor(zone) {
      const ghost = this.passport && (this.passport.ghosts || []).find(item => item.zone_key === zone.key);
      return ghost ? ghost.image_url || "" : "";
    },
    async loadMedia({ includePassport = true } = {}) {
      const generation = this.mediaGeneration;
      const actGeneration = ++this.actGeneration;
      const passportGeneration = includePassport ? ++this.passportGeneration : this.passportGeneration;
      const { booking_id: bookingId, car_id: carId } = this.localInspection;
      const results = await Promise.allSettled([
        damageControlApi.getAct(this.axios, bookingId),
        ...(includePassport ? [damageControlApi.getPassport(this.axios, carId)] : []),
      ]);
      if (this.disposed || generation !== this.mediaGeneration) return;
      if (actGeneration === this.actGeneration && results[0].status === "fulfilled") this.act = results[0].value;
      if (passportGeneration === this.passportGeneration && results[1] && results[1].status === "fulfilled") this.passport = results[1].value;
      const failed = results.find((result, index) => result.status === "rejected" &&
        (index === 0 ? actGeneration === this.actGeneration : passportGeneration === this.passportGeneration));
      if (failed) this.errorText = reportDamageControlFailure(this, failed.reason, { silent: true });
    },
    attemptsFor(zone) { return damageControlAttempts.history(this.localInspection.id, zone.key, zone.latest_shot); },
    rememberAttempt(key, shot) { damageControlAttempts.remember(this.localInspection.id, key, shot); },
    rememberPreview({ shotId, preview }) {
      if (this.previews[shotId]) URL.revokeObjectURL(this.previews[shotId]);
      this.$set(this.previews, shotId, URL.createObjectURL(preview));
    },
    releasePreviews() {
      Object.values(this.previews).forEach(url => URL.revokeObjectURL(url));
      this.previews = {};
    },
    selectZone(zone) { this.selectedKey = zone.key; },
    backToZones() { this.selectedKey = null; this.captureBusy = false; },
    close() {
      this.backToZones();
      this.visible = false;
      this.mediaGeneration += 1;
      this.releasePreviews();
      this.$emit("close");
    },
    updateInspection(payload) {
      const changed = (payload.zones || []).some(zone => {
        const old = (this.localInspection.zones || []).find(item => item.key === zone.key);
        return zone.current_shot && (!old || !old.current_shot || old.current_shot.id !== zone.current_shot.id);
      });
      const completed = payload.status === "completed" && this.localInspection.status !== "completed";
      this.localInspection = payload;
      this.$emit("update:inspection", payload);
      if (changed || completed) this.loadMedia({ includePassport: completed });
    },
    async complete() {
      if (this.completing || this.captureBusy || this.refreshRequired || this.blockers.length || this.localInspection.status !== "in_progress") return;
      this.completing = true;
      this.errorText = "";
      this.blockedZone = null;
      try {
        const payload = await damageControlApi.completeInspection(this.axios, this.localInspection.id);
        if (this.disposed) return;
        this.updateInspection(payload);
        this.backToZones();
        this.$emit("completed", payload);
      } catch (error) {
        if (this.disposed) return;
        const parsed = parseDamageControlError(error);
        const details = parsed && parsed.details;
        const zoneKey = details && (details.zone_key || (details.details && details.details.zone_key));
        try {
          const payload = await damageControlApi.getInspection(this.axios, this.localInspection.id);
          if (this.disposed) return;
          this.updateInspection(payload);
        } catch (_) { this.refreshRequired = true; }
        const blocker = this.blockers.find(item => item.zone_key === zoneKey) || this.blockers[0];
        this.blockedZone = zoneKey || (blocker && blocker.zone_key) || null;
        this.backToZones();
        this.errorText = blocker ? this.$t(blocker.reason) : reportDamageControlFailure(this, error, { silent: true });
      } finally { if (!this.disposed) this.completing = false; }
    },
  },
};
</script>

<style scoped>
.dc-inspection { max-height: 100%; }
.dc-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; word-break: normal; }
.dc-header > div { min-width: 0; }
.dc-zones { display: flex; flex-direction: column; gap: 12px; }
.dc-zone-row { width: 100%; padding: 16px; display: flex; align-items: center; gap: 16px; text-align: left; color: inherit; border: 1px solid #8886; border-radius: 8px; }
.dc-zone-row:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
.dc-zone-blocked { border: 2px solid var(--v-error-base, #b71c1c); }
.dc-zone-copy { min-width: 0; overflow-wrap: anywhere; }
.dc-hint, .dc-state { display: block; margin-top: 6px; }
.dc-required { display: inline-block; margin-left: 8px; font-size: 12px; }
.dc-thumbnail { width: 100px; height: 75px; object-fit: contain; flex-shrink: 0; }
.dc-blocker { text-align: left; color: var(--v-primary-base, #1565c0); padding: 8px 0; text-decoration: underline; }
.dc-footer { justify-content: flex-end; padding: 16px; }
@media (max-width: 599px) {
  .dc-header { padding: 16px; }
  .dc-header > div { width: 100%; }
  .dc-header, .dc-zone-row { flex-wrap: wrap; }
  .dc-header > .v-btn, .dc-footer > .v-btn { width: 100%; }
  .dc-thumbnail { width: 80px; height: 60px; }
}
</style>
